'use client';
import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Typography,
  Divider,
  Chip,
  Alert,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import Button from 'src/components/era-package/forms/Button';
import Buttons from 'src/components/era-package/era/Buttons';
import { getErrorDescription } from 'src/utils/rejectionErrorParser';

const LabelValue = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 110, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
};

/**
 * ErrorDetailModal
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {Object} error  - { code, message, isPga, agencyNum, pgaCode, programCode }
 * @param {Object} context - { type, position, refId }  (parent line item info)
 */
const ErrorDetailModal = ({ open, onClose, error, context }) => {
  const description = useMemo(() => {
    if (!error) return null;
    // Use messageType from parser (ES / CR / PGA) — not just PGA vs ES
    const messageType = error.messageType || (error.isPga ? 'PGA' : 'ES');
    return getErrorDescription(
      error.code,
      error.message,
      messageType,
      error.pgaCode || '',
      error.programCode || ''
    );
  }, [error]);

  if (!error) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="error-detail-title"
    >
      <DialogTitle
        id="error-detail-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2.5 }}
      >
        <Typography variant="h6">Error Details</Typography>
        <IconButton onClick={onClose} size="small" aria-label="close error detail modal">
          <Box component={FontAwesomeIcon} icon={faTimes} sx={{ width: '0.9em' }} />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 2.5, py: 2 }}>
        {/* Error Code & Message */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          <Chip
            label={error.code}
            size="small"
            color={error.isPga ? 'warning' : 'error'}
            variant="filled"
            sx={{ mt: 0.3, flexShrink: 0 }}
          />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {error.message}
          </Typography>
        </Box>

        {/* PGA Context — only for PGA errors */}
        {error.isPga && (error.pgaCode || error.programCode) && (
          <Box sx={{ mb: 2 }}>
            <Divider sx={{ mb: 1.5 }} />
            <LabelValue label="Agency" value={error.pgaCode} />
            <LabelValue label="Program" value={error.programCode} />
          </Box>
        )}

        {/* Description */}
        <Divider sx={{ mb: 1.5 }} />
        {description ? (
          <Typography variant="body2" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
            {description}
          </Typography>
        ) : (
          <Alert severity="info" sx={{ mt: 0.5 }}>
            No description found for this error code.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ pt: 1 }}>
        <Buttons
          advance={
            <Button onClick={onClose} variant="contained" title="Close error detail">
              Close
            </Button>
          }
        />
      </DialogActions>
    </Dialog>
  );
};

export default ErrorDetailModal;
